import { useMemo } from 'react';
import {
  UseDiningPaxHeadsUpMessageParams,
  UseDiningPaxHeadsUpMessageResult,
} from '@msc-b2c/interfaces/interfaces';
import { DiningFlowEntryPoint } from '@msc-b2c/interfaces/enums';

/**
 * Resolves the heads-up banner shown in dining pax selection steps.
 *
 * Behavior summary:
 * - CART entry point: disabled (no heads-up banner)
 * - ALREADY_PURCHASED: prefer booked variant when configured
 * - PRODUCT_CARD:
 *   - booked passengers -> booked variant
 *   - otherwise in cart -> in-cart variant
 */
export const useDiningPaxHeadsUpMessage = ({
  entryPoint,
  diningHeadsUpMessagesConfig,
  selectedObsCode,
  cartItemCodes,
  hasBookedPassengers,
}: UseDiningPaxHeadsUpMessageParams): UseDiningPaxHeadsUpMessageResult => {
  const paxHeadsUpMessage = useMemo(() => {
    if (!diningHeadsUpMessagesConfig || entryPoint === DiningFlowEntryPoint.CART) {
      return undefined;
    }

    const bookedVariant = () => {
      if (!diningHeadsUpMessagesConfig.bookedTitle && !diningHeadsUpMessagesConfig.bookedBody) {
        return undefined;
      }

      return {
        icon: diningHeadsUpMessagesConfig.bookedIcon,
        title: diningHeadsUpMessagesConfig.bookedTitle,
        body: diningHeadsUpMessagesConfig.bookedBody,
        contentColor: diningHeadsUpMessagesConfig.bookedContentColor,
        backgroundColor: diningHeadsUpMessagesConfig.bookedBackgroundColor,
      };
    };

    const inCartVariant = () => {
      if (!diningHeadsUpMessagesConfig.inCartTitle && !diningHeadsUpMessagesConfig.inCartBody) {
        return undefined;
      }

      return {
        icon: diningHeadsUpMessagesConfig.inCartIcon,
        title: diningHeadsUpMessagesConfig.inCartTitle,
        body: diningHeadsUpMessagesConfig.inCartBody,
        contentColor: diningHeadsUpMessagesConfig.inCartContentColor,
        backgroundColor: diningHeadsUpMessagesConfig.inCartBackgroundColor,
      };
    };

    if (entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) {
      return bookedVariant();
    }

    if (entryPoint === DiningFlowEntryPoint.PRODUCT_CARD) {
      if (hasBookedPassengers) {
        return bookedVariant();
      }

      const isInCart = !!selectedObsCode && cartItemCodes.includes(selectedObsCode);
      if (isInCart) {
        return inCartVariant();
      }
    }

    return undefined;
  }, [
    cartItemCodes,
    diningHeadsUpMessagesConfig,
    entryPoint,
    hasBookedPassengers,
    selectedObsCode,
  ]);

  return { paxHeadsUpMessage };
};
